import { Fragment, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition, Tab, Listbox } from '@headlessui/react';
import {
  ArrowLeftIcon,
  PlusIcon,
  ArchiveBoxIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ChevronUpDownIcon,
  CheckIcon,
  ChevronDownIcon,
  MagnifyingGlassCircleIcon,
  ClipboardDocumentListIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { policyApi } from '../lib/api';
import PolicyHygienePanel from '../components/policy/PolicyHygienePanel';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import { StatusBadge, EmptyState, LoadingSpinner } from '../components/ui';
import type { BadgeVariant } from '../components/ui';

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  ACTIVE: 'success',
  DRAFT: 'draft',
  UNDER_REVIEW: 'info',
  ARCHIVED: 'neutral',
  EXPIRED: 'danger',
};

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Active',
  DRAFT: 'Draft',
  UNDER_REVIEW: 'Under Review',
  ARCHIVED: 'Archived',
  EXPIRED: 'Expired',
};

const CATEGORY_LABELS: Record<string, string> = {
  COMPLIANCE: 'Compliance',
  LENDING: 'Lending',
  BSA_AML: 'BSA/AML',
  INFORMATION_SECURITY: 'Information Security',
  PRIVACY: 'Privacy',
  FAIR_LENDING: 'Fair Lending',
  VENDOR_MANAGEMENT: 'Vendor Management',
  DEPOSITS: 'Deposits',
  HR: 'Human Resources',
  OPERATIONS: 'Operations',
  MARKETING: 'Marketing',
  OTHER: 'Other',
};

const REVIEW_DECISIONS = [
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'NEEDS_CHANGES', label: 'Needs Changes' },
];

const REVIEW_BADGE_VARIANT: Record<string, BadgeVariant> = {
  APPROVED: 'success',
  REJECTED: 'danger',
  NEEDS_CHANGES: 'warning',
};

const DETAIL_TABS = ['Current Version', 'Version History', 'Reviews', 'Gap Analysis', 'Hygiene'] as const;

// ─── Component ───────────────────────────────────────────────────────────────

export default function PolicyDetailPage() {
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const { teamId } = useTeamId();

  const [showVersionModal, setShowVersionModal] = useState(false);
  const [versionForm, setVersionForm] = useState({ content: '', changeSummary: '' });
  const [expandedVersion, setExpandedVersion] = useState<string | null>(null);
  const [reviewForm, setReviewForm] = useState({ decision: 'APPROVED', comments: '' });
  const [gapResults, setGapResults] = useState<any>(null);

  // ── Query: policy detail ─────────────────────────────────────────────────

  const { data: policyData, isLoading } = useQuery({
    queryKey: ['policy', teamId, id],
    queryFn: () => policyApi.get(teamId!, id!),
    enabled: !!teamId && !!id,
  });
  const policy = policyData?.data;

  // ── Mutations ────────────────────────────────────────────────────────────

  const createVersionMutation = useMutation({
    mutationFn: (data: { content: string; changeSummary: string }) =>
      policyApi.createVersion(teamId!, id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policy', teamId, id] });
      toast.success('Version created successfully');
      setShowVersionModal(false);
      setVersionForm({ content: '', changeSummary: '' });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to create version');
    },
  });

  const submitReviewMutation = useMutation({
    mutationFn: () => policyApi.submitReview(teamId!, id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policy', teamId, id] });
      toast.success('Policy submitted for review');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to submit for review');
    },
  });

  const createReviewMutation = useMutation({
    mutationFn: (data: { versionId: string; decision: string; comments: string }) =>
      policyApi.createReview(teamId!, id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policy', teamId, id] });
      toast.success('Review submitted');
      setReviewForm({ decision: 'APPROVED', comments: '' });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to submit review');
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => policyApi.archive(teamId!, id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policy', teamId, id] });
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      toast.success('Policy archived');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to archive policy');
    },
  });

  const activateMutation = useMutation({
    mutationFn: () => policyApi.activate(teamId!, id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policy', teamId, id] });
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      toast.success('Policy activated');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to activate policy');
    },
  });

  const gapAnalysisMutation = useMutation({
    mutationFn: () => policyApi.gapAnalysis(teamId!, id!),
    onSuccess: (response) => {
      setGapResults(response.data);
      toast.success('Gap analysis complete');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to run gap analysis');
    },
  });

  // ── Policy → marketing rules (backlog #2) ──────────────────────────────────
  const [marketingCandidates, setMarketingCandidates] = useState<any[]>([]);
  const [selectedCandidates, setSelectedCandidates] = useState<Set<number>>(new Set());
  const [showMarketingRulesModal, setShowMarketingRulesModal] = useState(false);

  const previewMarketingRulesMutation = useMutation({
    mutationFn: () => policyApi.previewMarketingRules(teamId!, id!),
    onSuccess: (response) => {
      const candidates = response.data?.candidates ?? [];
      if (!candidates.length) {
        toast('No objectively checkable marketing rules were found in this policy.', { icon: 'ℹ️' });
        return;
      }
      setMarketingCandidates(candidates);
      setSelectedCandidates(new Set(candidates.map((_: any, i: number) => i)));
      setShowMarketingRulesModal(true);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to generate marketing rules');
    },
  });

  const saveMarketingRulesMutation = useMutation({
    mutationFn: () => {
      const chosen = marketingCandidates.filter((_, i) => selectedCandidates.has(i));
      return policyApi.saveMarketingRules(teamId!, id!, chosen);
    },
    onSuccess: (response) => {
      setShowMarketingRulesModal(false);
      toast.success(`Activated ${response.data?.created ?? 0} marketing rule(s) from this policy.`);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to save marketing rules');
    },
  });

  const toggleCandidate = (i: number) =>
    setSelectedCandidates((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  // ── Loading / Not Found ──────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="space-y-4">
        <Link
          to="/policies"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Policies
        </Link>
        <EmptyState
          icon={DocumentTextIcon}
          title="Policy not found"
          description="The policy you are looking for does not exist or has been removed."
        />
      </div>
    );
  }

  // ── Derived data ─────────────────────────────────────────────────────────

  const versions = policy.versions || [];
  const latestVersion = versions.length > 0 ? versions[0] : null;
  const reviews = policy.reviews || [];
  const status = policy.status || 'DRAFT';

  const selectedDecision = REVIEW_DECISIONS.find((d) => d.value === reviewForm.decision) || REVIEW_DECISIONS[0];

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <FeatureGate feature="policy_management">
      <div className="space-y-6">
        {/* Back link */}
        <Link
          to="/policies"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Policies
        </Link>

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-navy-900">{policy.title}</h1>
              <StatusBadge variant={STATUS_VARIANT[status] || 'neutral'}>
                {STATUS_LABEL[status] || status}
              </StatusBadge>
              <StatusBadge variant="info">
                {CATEGORY_LABELS[policy.category] || policy.category}
              </StatusBadge>
            </div>
            {policy.description && (
              <p className="mt-1 text-sm text-gray-500">{policy.description}</p>
            )}
            <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
              {policy.owner && <span>Owner: <span className="font-medium text-gray-700">{policy.owner}</span></span>}
              {policy.department && <span>Department: <span className="font-medium text-gray-700">{policy.department}</span></span>}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            {(status === 'ACTIVE' || status === 'DRAFT') && (
              <button
                onClick={() => archiveMutation.mutate()}
                disabled={archiveMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <ArchiveBoxIcon className="h-4 w-4" />
                {archiveMutation.isPending ? 'Archiving...' : 'Archive'}
              </button>
            )}
            {status !== 'ACTIVE' && (
              <button
                onClick={() => activateMutation.mutate()}
                disabled={activateMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-500 transition-colors disabled:opacity-50"
              >
                <CheckCircleIcon className="h-4 w-4" />
                {activateMutation.isPending ? 'Activating...' : 'Activate'}
              </button>
            )}
          </div>
        </div>

        {/* Date info row */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="rounded-lg border border-gray-100 bg-white p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Effective</p>
            <p className="mt-1 text-sm font-semibold text-navy-900">
              {policy.effectiveDate ? new Date(policy.effectiveDate).toLocaleDateString() : '—'}
            </p>
          </div>
          <div className="rounded-lg border border-gray-100 bg-white p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Expiration</p>
            <p className="mt-1 text-sm font-semibold text-navy-900">
              {policy.expirationDate ? new Date(policy.expirationDate).toLocaleDateString() : '—'}
            </p>
          </div>
          <div className="rounded-lg border border-gray-100 bg-white p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Last Reviewed</p>
            <p className="mt-1 text-sm font-semibold text-navy-900">
              {policy.lastReviewedAt ? new Date(policy.lastReviewedAt).toLocaleDateString() : '—'}
            </p>
          </div>
          <div className="rounded-lg border border-gray-100 bg-white p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Next Review</p>
            <p className="mt-1 text-sm font-semibold text-navy-900">
              {policy.nextReviewDate ? new Date(policy.nextReviewDate).toLocaleDateString() : '—'}
            </p>
          </div>
        </div>

        {/* Tab group */}
        <Tab.Group>
          <Tab.List className="flex space-x-1 border-b border-gray-200">
            {DETAIL_TABS.map((tab) => (
              <Tab
                key={tab}
                className={({ selected }) =>
                  clsx(
                    'whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors focus:outline-none',
                    selected
                      ? 'border-teal-600 text-teal-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                  )
                }
              >
                {tab}
              </Tab>
            ))}
          </Tab.List>

          <Tab.Panels className="mt-4">
            {/* ── Current Version Tab ────────────────────────────────────── */}
            <Tab.Panel>
              <div className="rounded-lg border border-gray-100 bg-white shadow-sm">
                {!latestVersion ? (
                  <EmptyState
                    icon={DocumentTextIcon}
                    title="No versions yet"
                    description="Create the first version of this policy to get started."
                    action={
                      <button
                        onClick={() => setShowVersionModal(true)}
                        className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500"
                      >
                        <PlusIcon className="h-4 w-4" />
                        Create First Version
                      </button>
                    }
                  />
                ) : (
                  <div className="p-6">
                    <div className="mb-4 flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-navy-900">
                          Version {latestVersion.versionNumber || latestVersion.version || 1}
                        </h3>
                        {latestVersion.changeSummary && (
                          <p className="mt-0.5 text-xs text-gray-500">{latestVersion.changeSummary}</p>
                        )}
                      </div>
                      <button
                        onClick={() => setShowVersionModal(true)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-500 transition-colors"
                      >
                        <PlusIcon className="h-4 w-4" />
                        Create New Version
                      </button>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans leading-relaxed">
                        {latestVersion.content}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </Tab.Panel>

            {/* ── Version History Tab ────────────────────────────────────── */}
            <Tab.Panel>
              <div className="rounded-lg border border-gray-100 bg-white shadow-sm">
                {versions.length === 0 ? (
                  <EmptyState
                    icon={ClipboardDocumentListIcon}
                    title="No version history"
                    description="Version history will appear here once you create your first version."
                  />
                ) : (
                  <div className="divide-y divide-gray-100">
                    {[...versions].reverse().reverse().map((ver: any, idx: number) => {
                      const versionNumber = ver.versionNumber || ver.version || versions.length - idx;
                      const isExpanded = expandedVersion === ver.id;
                      return (
                        <div key={ver.id} className="p-4">
                          <button
                            onClick={() => setExpandedVersion(isExpanded ? null : ver.id)}
                            className="flex w-full items-center justify-between text-left"
                          >
                            <div className="flex items-center gap-3">
                              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-teal-50 text-xs font-bold text-teal-700">
                                v{versionNumber}
                              </span>
                              <div>
                                <p className="text-sm font-medium text-navy-900">
                                  {ver.changeSummary || `Version ${versionNumber}`}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {ver.createdBy && <span>by {ver.createdBy} &middot; </span>}
                                  {ver.createdAt ? new Date(ver.createdAt).toLocaleDateString() : '—'}
                                </p>
                              </div>
                            </div>
                            <ChevronDownIcon
                              className={clsx(
                                'h-4 w-4 text-gray-400 transition-transform',
                                isExpanded && 'rotate-180',
                              )}
                            />
                          </button>
                          {isExpanded && ver.content && (
                            <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans leading-relaxed">
                                {ver.content}
                              </pre>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Tab.Panel>

            {/* ── Reviews Tab ────────────────────────────────────────────── */}
            <Tab.Panel>
              <div className="space-y-4">
                {/* Action: Submit for Review or Review Form */}
                {status === 'DRAFT' && (
                  <div className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm">
                    <p className="text-sm text-gray-600 mb-3">
                      This policy is in draft status. Submit it for review when ready.
                    </p>
                    <button
                      onClick={() => submitReviewMutation.mutate()}
                      disabled={submitReviewMutation.isPending}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50 transition-colors"
                    >
                      <ShieldCheckIcon className="h-4 w-4" />
                      {submitReviewMutation.isPending ? 'Submitting...' : 'Submit for Review'}
                    </button>
                  </div>
                )}

                {status === 'UNDER_REVIEW' && (
                  <div className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm">
                    <h3 className="text-sm font-semibold text-navy-900 mb-3">Review This Policy</h3>
                    <div className="space-y-3">
                      {/* Decision Listbox */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Decision</label>
                        <Listbox
                          value={reviewForm.decision}
                          onChange={(value) => setReviewForm({ ...reviewForm, decision: value })}
                        >
                          <div className="relative mt-1">
                            <Listbox.Button className="relative w-full cursor-pointer rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
                              <span className="block truncate">{selectedDecision.label}</span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                              </span>
                            </Listbox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {REVIEW_DECISIONS.map((dec) => (
                                  <Listbox.Option
                                    key={dec.value}
                                    value={dec.value}
                                    className={({ active }) =>
                                      clsx(
                                        'relative cursor-pointer select-none py-2 pl-10 pr-4 text-sm',
                                        active ? 'bg-teal-50 text-teal-700' : 'text-gray-900',
                                      )
                                    }
                                  >
                                    {({ selected }) => (
                                      <>
                                        <span className={clsx('block truncate', selected && 'font-medium')}>
                                          {dec.label}
                                        </span>
                                        {selected && (
                                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-teal-600">
                                            <CheckIcon className="h-4 w-4" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        </Listbox>
                      </div>

                      {/* Comments */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Comments</label>
                        <textarea
                          value={reviewForm.comments}
                          onChange={(e) => setReviewForm({ ...reviewForm, comments: e.target.value })}
                          rows={3}
                          placeholder="Provide your review comments..."
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      </div>

                      <button
                        onClick={() => {
                          if (!latestVersion?.id) {
                            toast.error('No policy version found to review');
                            return;
                          }
                          createReviewMutation.mutate({ ...reviewForm, versionId: latestVersion.id });
                        }}
                        disabled={createReviewMutation.isPending}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50 transition-colors"
                      >
                        {createReviewMutation.isPending ? 'Submitting...' : 'Submit Review'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Review list */}
                <div className="rounded-lg border border-gray-100 bg-white shadow-sm">
                  {reviews.length === 0 ? (
                    <EmptyState
                      icon={ShieldCheckIcon}
                      title="No reviews yet"
                      description="Reviews will appear here once the policy has been reviewed."
                    />
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {reviews.map((review: any) => (
                        <div key={review.id} className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-navy-900">
                                {review.reviewer || review.reviewerId || 'Reviewer'}
                              </span>
                              <StatusBadge variant={REVIEW_BADGE_VARIANT[review.decision] || 'neutral'}>
                                {review.decision}
                              </StatusBadge>
                            </div>
                            <span className="text-xs text-gray-500">
                              {review.createdAt ? new Date(review.createdAt).toLocaleDateString() : '—'}
                            </span>
                          </div>
                          {review.comments && (
                            <p className="mt-2 text-sm text-gray-600">{review.comments}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Tab.Panel>

            {/* ── Gap Analysis Tab ───────────────────────────────────────── */}
            <Tab.Panel>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-navy-900">Regulatory Gap Analysis</h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => previewMarketingRulesMutation.mutate()}
                      disabled={previewMarketingRulesMutation.isPending}
                      title="Generate marketing-ad screening rules from this policy. You confirm which to activate."
                      className="inline-flex items-center gap-1.5 rounded-lg border border-teal-600 px-4 py-2 text-sm font-medium text-teal-700 hover:bg-teal-50 disabled:opacity-50 transition-colors"
                    >
                      <ShieldCheckIcon className="h-4 w-4" />
                      {previewMarketingRulesMutation.isPending ? 'Generating…' : 'Generate Marketing Rules'}
                    </button>
                    <button
                      onClick={() => gapAnalysisMutation.mutate()}
                      disabled={gapAnalysisMutation.isPending}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50 transition-colors"
                    >
                      <MagnifyingGlassCircleIcon className="h-4 w-4" />
                      {gapAnalysisMutation.isPending ? 'Analyzing...' : 'Run Gap Analysis'}
                    </button>
                  </div>
                </div>

                {gapAnalysisMutation.isPending && (
                  <div className="py-12">
                    <LoadingSpinner size="lg" />
                  </div>
                )}

                {!gapResults && !gapAnalysisMutation.isPending && (
                  <div className="rounded-lg border border-gray-100 bg-white shadow-sm">
                    <EmptyState
                      icon={MagnifyingGlassCircleIcon}
                      title="No gap analysis results"
                      description="Run a gap analysis to identify regulatory coverage gaps in this policy."
                    />
                  </div>
                )}

                {gapResults && !gapAnalysisMutation.isPending && (
                  <div className="space-y-4">
                    {/* Overall Assessment */}
                    {gapResults.overallAssessment && (
                      <div className="rounded-lg border border-gray-100 bg-white p-5 shadow-sm">
                        <h4 className="text-sm font-semibold text-navy-900 mb-2">Overall Assessment</h4>
                        <p className="text-sm text-gray-700 leading-relaxed">{gapResults.overallAssessment}</p>
                      </div>
                    )}

                    {/* Gaps */}
                    {gapResults.gaps && gapResults.gaps.length > 0 && (
                      <div className="rounded-lg border border-gray-100 bg-white p-5 shadow-sm">
                        <h4 className="text-sm font-semibold text-navy-900 mb-3">
                          Identified Gaps ({gapResults.gaps.length})
                        </h4>
                        <ul className="space-y-3">
                          {gapResults.gaps.map((gap: any, idx: number) => (
                            <li key={idx} className="flex items-start gap-3">
                              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-100 text-xs font-bold text-red-600">
                                {idx + 1}
                              </span>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {gap.title || gap.area || gap.description || gap}
                                </p>
                                {gap.detail && (
                                  <p className="mt-0.5 text-xs text-gray-500">{gap.detail}</p>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Recommendations */}
                    {gapResults.recommendations && gapResults.recommendations.length > 0 && (
                      <div className="rounded-lg border border-gray-100 bg-white p-5 shadow-sm">
                        <h4 className="text-sm font-semibold text-navy-900 mb-3">
                          Recommendations ({gapResults.recommendations.length})
                        </h4>
                        <ul className="space-y-3">
                          {gapResults.recommendations.map((rec: any, idx: number) => (
                            <li key={idx} className="flex items-start gap-3">
                              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-100 text-xs font-bold text-teal-700">
                                {idx + 1}
                              </span>
                              <p className="text-sm text-gray-700">
                                {typeof rec === 'string' ? rec : rec.description || rec.title || JSON.stringify(rec)}
                              </p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Tab.Panel>

            {/* ── Hygiene Tab (CLARID-SPEC-BSA-001 §3.1) ──────────────────── */}
            <Tab.Panel>
              <PolicyHygienePanel teamId={teamId!} policyId={id!} />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>

        {/* ── Policy → Marketing Rules confirm modal (backlog #2) ────────────── */}
        <Transition appear show={showMarketingRulesModal} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setShowMarketingRulesModal(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
              leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>
            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">
                    <Dialog.Title className="text-lg font-semibold text-navy-900">
                      Marketing rules from this policy
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-gray-600">
                      Review the proposed rules and choose which to activate. Activated rules run in every
                      marketing ad check and flag content as <span className="font-medium">against your policy</span>{' '}
                      (separate from regulatory findings). You can edit or remove them later under Custom Rules.
                    </p>

                    <div className="mt-4 max-h-[50vh] space-y-3 overflow-y-auto pr-1">
                      {marketingCandidates.map((c, i) => (
                        <label
                          key={i}
                          className={clsx(
                            'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
                            selectedCandidates.has(i) ? 'border-teal-400 bg-teal-50/50' : 'border-gray-200',
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={selectedCandidates.has(i)}
                            onChange={() => toggleCandidate(i)}
                            className="mt-1 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-900">{c.name}</p>
                              <span className={clsx(
                                'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase',
                                c.severity === 'CRITICAL' || c.severity === 'HIGH'
                                  ? 'bg-red-100 text-red-700'
                                  : c.severity === 'MEDIUM' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600',
                              )}>{c.severity}</span>
                            </div>
                            {c.requiredAction && (
                              <p className="mt-0.5 text-xs text-gray-600">{c.requiredAction}</p>
                            )}
                            <p className="mt-1 text-[11px] text-gray-500">
                              <span className="font-medium">Fires when:</span>{' '}
                              {c.triggerType === 'KEYWORD_MISSING' && <>ad is missing “{c.triggerValue}”</>}
                              {c.triggerType === 'KEYWORD_PRESENT' && <>ad contains “{c.triggerValue}”</>}
                              {c.triggerType === 'PATTERN_MATCH' && <>ad matches /{c.triggerValue}/</>}
                              {c.secondTriggerType && c.secondTriggerValue && (
                                <> and {c.secondTriggerType === 'KEYWORD_MISSING' ? 'is missing' : 'contains'} “{c.secondTriggerValue}”</>
                              )}
                            </p>
                            {c.policyBasis && (
                              <p className="mt-1 border-l-2 border-gray-200 pl-2 text-[11px] italic text-gray-400">
                                “{c.policyBasis}”
                              </p>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>

                    <div className="mt-5 flex items-center justify-between">
                      <span className="text-xs text-gray-500">{selectedCandidates.size} of {marketingCandidates.length} selected</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowMarketingRulesModal(false)}
                          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => saveMarketingRulesMutation.mutate()}
                          disabled={selectedCandidates.size === 0 || saveMarketingRulesMutation.isPending}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
                        >
                          <ShieldCheckIcon className="h-4 w-4" />
                          {saveMarketingRulesMutation.isPending ? 'Activating…' : `Activate ${selectedCandidates.size} rule(s)`}
                        </button>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* ── Create Version Modal ───────────────────────────────────────── */}
        <Transition appear show={showVersionModal} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setShowVersionModal(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
                    <Dialog.Title className="text-lg font-semibold text-navy-900">
                      Create New Version
                    </Dialog.Title>

                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Content <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={versionForm.content}
                          onChange={(e) => setVersionForm({ ...versionForm, content: e.target.value })}
                          rows={10}
                          placeholder="Enter the full policy content for this version..."
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500 font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Change Summary
                        </label>
                        <input
                          type="text"
                          value={versionForm.changeSummary}
                          onChange={(e) => setVersionForm({ ...versionForm, changeSummary: e.target.value })}
                          placeholder="Brief summary of changes in this version"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={() => setShowVersionModal(false)}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => createVersionMutation.mutate(versionForm)}
                        disabled={!versionForm.content.trim() || createVersionMutation.isPending}
                        className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50 transition-colors"
                      >
                        {createVersionMutation.isPending ? 'Creating...' : 'Create Version'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </FeatureGate>
  );
}
